/**
 * "What's New" announcements registry.
 *
 * The Home screen shows the newest announcement whose `version` is higher
 * than the highest version Eva has dismissed (settings.lastAnnouncementSeen
 * — server-side, so it shows once across devices). On dismiss it persists
 * LATEST_ANNOUNCEMENT_VERSION.
 *
 * To announce a future update: add an entry with a higher version. The
 * modal re-appears once with that content, then sleeps again. No other
 * wiring needed.
 */

/** Picks which preview art the modal shows for an announcement. */
export type AnnouncementKind = "new-stickers" | "streak-abc" | "year-2";

export interface Announcement {
  version: number;
  /** Drives the modal's preview block (see WhatsNewModal). */
  kind: AnnouncementKind;
  /** Short, friendly title. */
  headline: string;
  /** One line at a 6-year-old reading level. */
  body: string;
}

export const ANNOUNCEMENTS: readonly Announcement[] = [
  {
    version: 1,
    kind: "new-stickers",
    headline: "Look what's new!",
    body: "New stickers to collect - and a whole alphabet to fill up!",
  },
  {
    version: 2,
    kind: "streak-abc",
    headline: "Look what's new!",
    body: "Count your days in a row - and you've got new ABC letters too!",
  },
  {
    version: 3,
    kind: "year-2",
    headline: "Hello, Year 2!",
    body: "You finished Year 1! Your new sticker is waiting in My Stickers.",
  },
];

export const LATEST_ANNOUNCEMENT_VERSION = ANNOUNCEMENTS.reduce(
  (max, a) => Math.max(max, a.version),
  0,
);

/** The newest announcement Eva hasn't seen yet, or null if she's current. */
export function latestUnseenAnnouncement(seen: number): Announcement | null {
  let best: Announcement | null = null;
  for (const a of ANNOUNCEMENTS) {
    if (a.version > seen && (best === null || a.version > best.version)) {
      best = a;
    }
  }
  return best;
}
